import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from '../../../util/reactIntl';
import { formatDateRangeCompactShort, getInclusiveEndDate } from '../../../util/dateRangeHelpers';
import { saveRentalDefaults, pickRentalDefaultsFromValues } from '../../../util/rentalDefaults';
import DateRangePicker from '../Sidebar/DateRangePicker/DateRangePicker';
import CategoryPicker from './CategoryPicker/CategoryPicker';
import StoreProfileDesktopFiltersModal from './StoreProfileDesktopFiltersModal';
import css from './DesktopResultsFiltersBar.module.css';

const hasEffectiveModalFilters = listingFacetFilters => {
  const filters = listingFacetFilters || {};
  return Object.entries(filters).some(([key, value]) => {
    if (value === null || value === undefined) return false;
    const normalized = `${value}`.trim();
    if (!normalized) return false;

    if (key === 'priceRange') {
      const [min, max] = normalized.split(',');
      const minValue = Number(min);
      const maxValue = Number(max);
      if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) return false;
      return minValue >= 0 && maxValue >= 0 && minValue < maxValue;
    }

    return true;
  });
};

const DesktopResultsFiltersBar = props => {
  const {
    desktopSearchBar,
    user,
    selectedCategoryFilter,
    onCategoryChange,
    dateRangeFilter,
    onDateRangeChange,
    storeTimeZone,
    dateRangePickerRef,
    datePickerAnchorRef,
    onScrollToDateField,
    onManageDisableScrolling,
    listingFacetFilters = {},
    onListingFacetFiltersChange,
    scrollActiveCategory,
  } = props;
  const hasAppliedListingFilters = hasEffectiveModalFilters(listingFacetFilters);

  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [isDatePickerExpanded, setIsDatePickerExpanded] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : false
  );
  const trackIfInViewRef = useRef(null);
  // Sentinel starts in view at mount; avoid collapsing the date picker before observer runs.
  const [isTrackIfInView, setIsTrackIfInView] = useState(true);
  const stickyStateTimerRef = useRef(null);
  const hasDateSelection = Boolean(dateRangeFilter?.startDate && dateRangeFilter?.endDate);
  // When dates are selected, hide date inputs and show compact label + CTA
  // regardless of whether the bar is currently in the "sticky" scroll state.
  const showStickyDateContext = hasDateSelection && !isDatePickerExpanded;
  const showDatePicker = !hasDateSelection || isDatePickerExpanded;
  const compactDateText = formatDateRangeCompactShort(
    dateRangeFilter?.startDate,
    getInclusiveEndDate(dateRangeFilter?.endDate, storeTimeZone),
    storeTimeZone
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!isDesktop) return undefined;
    if (typeof window === 'undefined') return undefined;
    if (!trackIfInViewRef.current) return undefined;

    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;
        const nextInView = Boolean(entry?.isIntersecting);
        if (stickyStateTimerRef.current) {
          clearTimeout(stickyStateTimerRef.current);
        }
        // Small debounce smooths out rapid flips near the sticky threshold.
        stickyStateTimerRef.current = setTimeout(() => {
          setIsTrackIfInView(nextInView);
        }, 90);
      },
      // Account for the sticky bar covering the top portion of the viewport.
      // When the sentinel is effectively behind the sticky header, show compact date context.
      { root: null, threshold: 0, rootMargin: '-8px 0px 0px 0px' }
    );

    observer.observe(trackIfInViewRef.current);
    return () => {
      observer.disconnect();
      if (stickyStateTimerRef.current) {
        clearTimeout(stickyStateTimerRef.current);
      }
    };
  }, [isDesktop]);

  if (!isDesktop) {
    return null;
  }

  const handleChangeDatesClick = () => {
    setIsDatePickerExpanded(true);
    window.requestAnimationFrame(() => {
      onScrollToDateField?.();
      dateRangePickerRef?.current?.focus?.();
    });
  };

  // The calendar was dismissed without a new complete range (tap outside, Apply with
  // nothing picked). The applied dates are still in force, so fold the picker back into
  // the compact "{dates} · Change Dates" row instead of leaving an open, empty-looking
  // picker sitting above results that are still filtered by the old range.
  const handlePickerClose = () => {
    setIsDatePickerExpanded(false);
  };

  const handleDateRangeChange = dates => {
    onDateRangeChange(dates);
    if (dates?.startDate && dates?.endDate) {
      setIsDatePickerExpanded(false);
      // Becomes the new default everywhere a date picker prefills from
      // util/rentalDefaults.js, even if this range is never booked - a genuine Apply
      // here is a real user gesture. Clearing (dates come through as null) skips this
      // gate, so it never fires from that path.
      saveRentalDefaults(pickRentalDefaultsFromValues({ bookingDates: dates }));
    }
  };

  return (
    <>
      <div id="track-if-in-view" ref={trackIfInViewRef} />
      <section className={[css.root, !isTrackIfInView ? css.isSticky : null, isFiltersModalOpen && css.isFiltersModalOpen].filter(Boolean).join(' ')}>
        <div
          ref={datePickerAnchorRef}
          className={[css.datePickerWrap, !showDatePicker ? css.datePickerHidden : null].filter(Boolean).join(' ')}
        >
          <DateRangePicker
            startDate={dateRangeFilter?.startDate || null}
            endDate={dateRangeFilter?.endDate || null}
            onDatesChange={handleDateRangeChange}
            minimumNights={0}
            timeZone={storeTimeZone}
            ref={dateRangePickerRef}
            onFieldFocusScroll={onScrollToDateField}
            onPickerClose={handlePickerClose}
          />
        </div>
        {showStickyDateContext && !isDatePickerExpanded ? (
          <div className={css.dateContextRow}>
            <span className={css.compactDateText}>{compactDateText}</span>
            <button type="button" className={css.changeDatesButton} onClick={handleChangeDatesClick}>
              <FormattedMessage id="Sidebar.changeDates" defaultMessage="Change Dates" />
            </button>
          </div>
        ) : null}
        <div className={css.topRow}>
          <div className={css.searchWrap}>{desktopSearchBar}</div>
          <button
            type="button"
            className={`${css.filtersButton} ${hasAppliedListingFilters ? css.filtersButtonActive : ''}`}
            onClick={() => setIsFiltersModalOpen(true)}
          >
            <i className={`ri-filter-line ${css.filtersMainIcon}`} />
            <FormattedMessage id="AlgoliaFilters.filters" defaultMessage="Filters" />
            {hasAppliedListingFilters ? (
              <i className={`ri-subtract-line ${css.filtersAppliedIcon}`} />
            ) : null}
          </button>
        </div>
        <CategoryPicker
          user={user}
          selectedCategoryFilter={selectedCategoryFilter}
          onCategoryChange={onCategoryChange}
          scrollActiveCategory={scrollActiveCategory}
        />

        <StoreProfileDesktopFiltersModal
          isOpen={isFiltersModalOpen}
          onClose={() => setIsFiltersModalOpen(false)}
          onManageDisableScrolling={onManageDisableScrolling}
          initialListingFilters={listingFacetFilters}
          onApplyFilters={onListingFacetFiltersChange}
        />
      </section>
    </>
  );
};

DesktopResultsFiltersBar.propTypes = {
  desktopSearchBar: PropTypes.node,
  user: PropTypes.object.isRequired,
  selectedCategoryFilter: PropTypes.shape({
    categoryId: PropTypes.string,
    depth: PropTypes.number,
  }),
  onCategoryChange: PropTypes.func.isRequired,
  dateRangeFilter: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
  }),
  onDateRangeChange: PropTypes.func.isRequired,
  storeTimeZone: PropTypes.string,
  dateRangePickerRef: PropTypes.shape({ current: PropTypes.any }),
  datePickerAnchorRef: PropTypes.shape({ current: PropTypes.any }),
  onScrollToDateField: PropTypes.func,
  onManageDisableScrolling: PropTypes.func,
  listingFacetFilters: PropTypes.object,
  onListingFacetFiltersChange: PropTypes.func,
  scrollActiveCategory: PropTypes.string,
};

export default DesktopResultsFiltersBar;
